import { localizeWeekdayLabel } from '../i18n/assetFieldLabels';
import type { TranslateFn } from '../i18n/operationLabels';
import type { AppLanguage } from '../i18n/types';
import {
  OPENING_TIMES_WEEKDAY_ORDER,
  findOpeningTimesWeekdayKey,
} from './openingTimesWeekdays';
import { openingTimesNormalizeDayValue } from './openingTimesLocale';

/** Mon–Fri (US) vs Sun–Thu (Israel) — when all share hours, show “Weekdays” / “ימי חול”. */
const LOCALE_WEEKDAY_KEYS: Record<AppLanguage, readonly string[]> = {
  en: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  he: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
};

/**
 * Cluster only within each segment so Friday (IL) / weekend days never merge into “ימי חול” / “Weekdays”
 * even when open hours match.
 */
/** Weekdays row + one row per weekend day when hours are uniform within each segment. */
const LOCALE_DAY_SEGMENTS: Record<AppLanguage, readonly (readonly string[])[]> = {
  en: [
    ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    ['saturday'],
    ['sunday'],
  ],
  he: [
    ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
    ['friday'],
    ['saturday'],
  ],
};

export type OpeningTimesDisplayRow = {
  /** Canonical weekday keys in calendar order. */
  days: readonly string[];
  rawValue: unknown;
};

function weekdaySignature(openingTimes: Record<string, unknown>, canonical: string): string {
  const actualKey = findOpeningTimesWeekdayKey(openingTimes, canonical);
  if (actualKey === undefined) {
    return '__missing__';
  }
  return JSON.stringify(openingTimesNormalizeDayValue(openingTimes[actualKey]));
}

function weekdayRawValue(openingTimes: Record<string, unknown>, canonical: string): unknown {
  const actualKey = findOpeningTimesWeekdayKey(openingTimes, canonical);
  if (actualKey === undefined) {
    return undefined;
  }
  return openingTimes[actualKey];
}

function clusterDaysInSegment(
  openingTimes: Record<string, unknown>,
  segment: readonly string[],
): OpeningTimesDisplayRow[] {
  const clusters = new Map<
    string,
    { days: string[]; rawValue: unknown; firstIndex: number }
  >();

  for (const canonical of segment) {
    const index = OPENING_TIMES_WEEKDAY_ORDER.indexOf(
      canonical as (typeof OPENING_TIMES_WEEKDAY_ORDER)[number],
    );
    const signature = weekdaySignature(openingTimes, canonical);
    const rawValue = weekdayRawValue(openingTimes, canonical);
    let cluster = clusters.get(signature);
    if (!cluster) {
      cluster = { days: [], rawValue, firstIndex: index };
      clusters.set(signature, cluster);
    }
    cluster.days.push(canonical);
  }

  return Array.from(clusters.values())
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map(({ days, rawValue }) => ({ days, rawValue }));
}

/**
 * Groups weekdays that share identical hours into display rows (calendar order preserved).
 * Friday (IL) and weekend days stay in separate rows from “ימי חול” / “Weekdays” even when hours match.
 */
export function groupOpeningTimesWeekdayDisplayRows(
  openingTimes: Record<string, unknown>,
  language: AppLanguage = 'en',
): OpeningTimesDisplayRow[] {
  const rows: OpeningTimesDisplayRow[] = [];
  for (const segment of LOCALE_DAY_SEGMENTS[language]) {
    rows.push(...clusterDaysInSegment(openingTimes, segment));
  }
  return rows;
}

/**
 * One row per calendar day (Sun–Sat) for consumer asset pages.
 * When any weekday is present, lists all seven days so matching hours still appear on each line.
 */
export function flattenOpeningTimesToDailyDisplayRows(
  openingTimes: Record<string, unknown>,
): OpeningTimesDisplayRow[] {
  const hasAnyWeekday = OPENING_TIMES_WEEKDAY_ORDER.some(
    (day) => findOpeningTimesWeekdayKey(openingTimes, day) !== undefined,
  );
  if (!hasAnyWeekday) {
    return [];
  }
  return OPENING_TIMES_WEEKDAY_ORDER.map((canonical) => ({
    days: [canonical],
    rawValue: weekdayRawValue(openingTimes, canonical),
  }));
}

function sameWeekdayKeySet(days: readonly string[], expected: readonly string[]): boolean {
  if (days.length !== expected.length) {
    return false;
  }
  const set = new Set(days.map((d) => d.trim().toLowerCase()));
  return expected.every((d) => set.has(d));
}

function isLocaleWeekdaySet(days: readonly string[], language: AppLanguage): boolean {
  return sameWeekdayKeySet(days, LOCALE_WEEKDAY_KEYS[language]);
}

/**
 * Display lines for a grouped opening-hours row: locale “Weekdays”, all days, or one line per day.
 */
export function formatOpeningTimesDayGroupLines(
  days: readonly string[],
  t: TranslateFn,
  language: AppLanguage,
): string[] {
  if (days.length === OPENING_TIMES_WEEKDAY_ORDER.length) {
    return [t('assetDetail.allDays')];
  }
  if (isLocaleWeekdaySet(days, language)) {
    return [t('openingTimes.weekdaysSection')];
  }
  return days.map((day) => localizeWeekdayLabel(day, t));
}
